import React, { useCallback, useState, useEffect, FC } from 'react';
import _ from 'lodash';
import { makeStyles } from 'design-system/styles';
import { Button, RadioGroup, Radio, Select, Option, Spinner } from 'design-system';
import {
  RealReviewLevel,
  getSanitizedReviewLevel,
  ReviewTier,
  ReviewLevel,
} from '@scale/shared/defaults';
import { useApiFetchGetRequest } from 'react-scale/hooks/useApiFetchGetRequest';
import apiFetch, { handleApiFetchError } from '@scale/frontend-lib/api-fetch';
import { TeamAssignmentsResponse } from '@scale/shared/types/api/corp-api/workerTeams';
import { DialogActions } from 'react-scale/components/design-system/Dialog';
import { invokeCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import { STAGE, ALERT_CONTENT } from '../../constants';
import {
  PAY_CONFIG_SET_UP_REQUEST_FORM,
  SLACK_PAY_CHANNEL,
} from 'corp/components/PayGuardRail/constants';

interface AssignTeamViewProps {
  team: { id: string; name: string };
  setTeam: React.Dispatch<React.SetStateAction<{ id: string; name: string }>>;
  setCurrentStage: React.Dispatch<React.SetStateAction<STAGE>>;
  setAlertStatus: React.Dispatch<React.SetStateAction<{ severity: string; content: any } | null>>;
  setAddedWorkerTeams: React.Dispatch<
    React.SetStateAction<{ primary: Array<string>; secondary: Array<string> }>
  >;
  TeamSelector: () => JSX.Element;
  projectId?: string;
}

type ProjectItem = {
  projectId: string;
  reviewLevel?: ReviewLevel;
};
const AssignTeamView: FC<AssignTeamViewProps> = ({
  setCurrentStage,
  setAlertStatus,
  setAddedWorkerTeams,
  projectId,
  team,
  TeamSelector,
}) => {
  const classes = useStyle();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [tierMap, setTierMap] = useState<{
    [ReviewTier.primaryTeamAssignments]: Array<ProjectItem>;
    [ReviewTier.secondaryTeamAssignments]: Array<ProjectItem>;
  }>({
    primaryTeamAssignments: [],
    secondaryTeamAssignments: [],
  });

  const recallLevels = _.reverse([...RealReviewLevel]);
  type RecallableReviewLevel = (typeof RealReviewLevel)[number];

  const [reviewProps, setReviewProps] = useState<{
    tier: ReviewTier;
    level: RecallableReviewLevel | null;
  }>({ tier: ReviewTier.primaryTeamAssignments, level: null });

  const { loading, response } = useApiFetchGetRequest<TeamAssignmentsResponse>(
    `corp-api/worker_teams/team/${team.id}/team_assignments`,
  );

  useEffect(() => {
    if (loading === false && isFetching === false) {
      const primaryTeamAssignments: Array<ProjectItem> = [];
      const secondaryTeamAssignments: Array<ProjectItem> = [];

      response?.forEach(({ projectId, tier, reviewLevel }) => {
        if (tier === 'Primary') {
          if (!_.isNil(reviewLevel)) {
            primaryTeamAssignments.push({ projectId, reviewLevel });
          } else {
            primaryTeamAssignments.push({ projectId });
          }
        } else if (tier === 'Secondary') {
          if (!_.isNil(reviewLevel)) {
            secondaryTeamAssignments.push({ projectId, reviewLevel });
          } else {
            secondaryTeamAssignments.push({ projectId });
          }
        }
      });
      setTierMap({ primaryTeamAssignments, secondaryTeamAssignments });
    }
  }, [loading, isFetching]);

  const handleAssignment = useCallback(async () => {
    const newAssignment =
      reviewProps.level || reviewProps.level === 0
        ? { projectId, reviewLevel: reviewProps.level }
        : { projectId };
    setIsFetching(true);
    try {
      const res = await apiFetch(null, `internal/pay/guardrail`, {
        method: 'GET',
        query: newAssignment,
      });

      if (!res?.payConfigSetupDone) {
        setAlertStatus({
          severity: ALERT_CONTENT.error.severity,
          content: (
            <div>
              It looks like the pay configuration in this project is not setup for
              {!_.isNil(reviewProps.level)
                ? ' review level: ' + reviewProps.level
                : ' any review levels'}
              !
              <span style={{ margin: '2px 0', display: 'inline-block' }}>
                Please fill out{' '}
                <a rel="noopener noreferrer" target="_blank" href={PAY_CONFIG_SET_UP_REQUEST_FORM}>
                  this form
                </a>{' '}
                and our pay team will set it up. For questions, post in{' '}
                <a rel="noopener noreferrer" target="_blank" href={SLACK_PAY_CHANNEL}>
                  #ops-pay
                </a>
              </span>
            </div>
          ),
        });
        setIsFetching(false);
        return;
      }

      const job: Record<string, any> = await invokeCorpJob('updateTeamConfigField', {
        teamId: team.id,
        key: reviewProps.tier,
        value: [...tierMap[reviewProps.tier], newAssignment],
        isClear: false,
      });
      setIsFetching(false);
      if (job.status === 'completed') {
        if (!job.jsonOutput.success) {
          setAlertStatus({
            severity: ALERT_CONTENT.error.severity,
            content: job.jsonOutput.message,
          });
        } else {
          setAlertStatus(ALERT_CONTENT.assignSuccess);
          setAddedWorkerTeams(prevState => {
            const { primary, secondary } = prevState;
            if (reviewProps.tier === ReviewTier.primaryTeamAssignments) {
              return { primary: [...primary, team.name], secondary: [...secondary] };
            } else {
              return { primary: [...primary], secondary: [...secondary, team.name] };
            }
          });
          setCurrentStage(STAGE.MAIN_VIEW);
        }
      } else {
        const results: any | undefined = job.jsonOutput;
        if (results) {
          setAlertStatus({ severity: 'error', content: results.message });
        } else {
          setAlertStatus(ALERT_CONTENT.error);
        }
      }
    } catch (error) {
      setIsFetching(false);
      setAlertStatus({ severity: 'error', content: error.response?.message || error });
      handleApiFetchError(error, 'Failed to assign team a project.');
    }
    setTimeout(() => {
      setAlertStatus(null);
    }, 1000 * 10);
  }, [
    reviewProps.level,
    reviewProps.tier,
    projectId,
    team.id,
    team.name,
    tierMap,
    setAlertStatus,
    setAddedWorkerTeams,
    setCurrentStage,
  ]);

  return (
    <div className={classes.container}>
      <fieldset className="fieldset fieldset--long">
        <label>Choose Worker Team</label>
        <TeamSelector />
      </fieldset>
      {loading && <Spinner size="sm" />}
      <fieldset className="fieldset fieldset--long">
        <label>Review Tier</label>
        <RadioGroup
          onChange={selectedOrder =>
            setReviewProps({
              ...reviewProps,
              tier: selectedOrder as ReviewTier,
            })
          }
          value={reviewProps.tier}
          row
        >
          <Radio
            value={ReviewTier.primaryTeamAssignments}
            labelPlacement="end"
            label="Primary"
            disabled={false}
          />
          <Radio
            value={ReviewTier.secondaryTeamAssignments}
            labelPlacement="end"
            label="Secondary"
            disabled={false}
          />
        </RadioGroup>
      </fieldset>
      <fieldset className="fieldset fieldset--long">
        <label>Review Level</label>
        <Select
          label="Review Level"
          value={reviewProps.level}
          onChange={e => {
            setReviewProps({
              ...reviewProps,
              level: e.target.value as RecallableReviewLevel | null,
            });
          }}
        >
          {recallLevels.map(val => (
            <Option value={val} key={val}>
              {getSanitizedReviewLevel(val)}
            </Option>
          ))}
          <Option value={null} key={'all'}>
            All Layers
          </Option>
        </Select>
      </fieldset>
      <DialogActions className={classes.actionWraper}>
        <Button
          type="primary"
          startIcon={isFetching ? <Spinner size="sm" /> : null}
          disabled={loading || isFetching}
          onClick={handleAssignment}
        >
          Assign
        </Button>
      </DialogActions>
    </div>
  );
};

const useStyle = makeStyles({
  container: {
    width: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& label': {
      padding: '10px 0',
      fontSize: 16,
      fontWeight: 'bold',
    },
  },
  actionWraper: {
    width: '100%',
  },
});

export default AssignTeamView;
